import {Button, Divider, Stack, Typography, capitalize, useTheme} from '@mui/material';
import {Authorization} from 'domain/usecases';
import {StyledCard} from 'presentation/components/StyledCard';
import {translations} from 'presentation/translations-new';
import React, {Fragment, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import Icon from 'presentation/icons';
import {FeaturesEnum, MyAppointmentsEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {TagChip} from 'presentation/components';
import {AuthorizationStatusEnum, Status, cardStyleByStatus} from '../../constants';

import {useDenialLetterHandler} from '../../hooks/useDenialLetterHandler';
import {usePrintGuideHandler} from '../../hooks/usePrintGuideHandler';
import {getStatusList} from '../../utils';
import {DownloadDocument, PasswordSection} from '../AuthorizationCardComponents';
import {AuthorizationCardContainer} from './styles';
import {AuthStatusDrawer} from '../AuthStatusDrawer';

const {authorizationCard} = translations['pt-br'].privateFlow.requestAuthorizations;
const {seeMore} = translations['pt-br'].commons;

export function AuthorizationCard(authorization: Authorization) {
	const [selectedAuthorization, setSelectedAuthorization] = useState<Authorization | null>(null);

	const navigate = useNavigate();
	const theme = useTheme();
	const {borderColor, chipText, chipVariant, chipTextColor, chipBackgroundColor} =
		cardStyleByStatus(theme, authorization?.dsSituacao as Status);

	const {isEnabled: isTrilhaAutorizacaoEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_TRILHA_AUTORIZACAO,
	);

	const statusList = getStatusList(authorization);
	const isDenialLetterAvailable =
		authorization?.dsSituacao === AuthorizationStatusEnum.REJECTED && authorization?.nuRegistro;
	const hasStatusHistory = authorization?.trilhaStatus?.length > 0;

	const {getPrintGuideUrl, isFetchingPrintGuide} = usePrintGuideHandler({
		cdPrestador: authorization?.cdPrestador,
		cdSenha: authorization?.cdAutorizacaoSenha,
	});

	const {downloadDenialLetter, isFetchingDenialLetter} = useDenialLetterHandler({
		nu_controle: authorization?.nuRegistro,
	});

	const onOpenStatusDrawer = () => {
		setSelectedAuthorization(authorization);
	};

	const onCloseStatusDrawer = () => {
		setSelectedAuthorization(null);
	};

	const isDrawerOpen = selectedAuthorization?.cdProcedimento === authorization?.cdProcedimento;
	const shouldRenderDrawer = isTrilhaAutorizacaoEnabled && hasStatusHistory && isDrawerOpen;

	const showSeeMyAppointments =
		authorization?.dsSituacao === AuthorizationStatusEnum.SUCCESS ||
		authorization?.dsSituacao === AuthorizationStatusEnum.FINISHED;

	return (
		<Fragment>
			<AuthorizationCardContainer $borderColor={borderColor}>
				<Stack gap={1}>
					<Stack>
						<Typography variant="h6" fontWeight={400} color="primary">
							{capitalize(authorization?.nmProcedimento.toLowerCase())}
						</Typography>
					</Stack>

					<Stack direction="row" alignItems="center" justifyContent={'space-between'}>
						<TagChip
								label={chipText}
								color={chipVariant}
								usePrimaryTextColor={false}
								sx={{
									color: chipTextColor,
									backgroundColor: chipBackgroundColor,
									'& .MuiChip-label': {color: chipTextColor},
								}}
							/>

						{isTrilhaAutorizacaoEnabled && hasStatusHistory ? (
							<Button
								variant="text"
								onClick={onOpenStatusDrawer}
								data-tag-id={AutoSolEnum.CLICK_VER_MAIS_HISTORICO_AUTOSOL}
								endIcon={
									<Icon
										name="chevron-right-outlined"
										color={theme.palette.primary.main}
										size={20}
									/>
								}>
								{seeMore}
							</Button>
						) : null}
					</Stack>

					<Divider />

					<StyledCard.Content sx={{flexDirection: 'column'}} list={statusList} />
				</Stack>

				{authorization?.dsSituacao !== AuthorizationStatusEnum.REJECTED ? (
					<PasswordSection
						password={
							authorization?.dsSituacao === AuthorizationStatusEnum.SUCCESS
								? authorization?.cdSenha
								: undefined
						}
						validate={authorization?.dtValidade}
					/>
				) : null}

				{authorization?.dsSituacao === AuthorizationStatusEnum.SUCCESS ? (
					<DownloadDocument
						label={authorizationCard.downloadLabel1}
						onClick={getPrintGuideUrl}
						loading={isFetchingPrintGuide}
					/>
				) : null}

				{isDenialLetterAvailable ? (
					<Fragment>
						<DownloadDocument
							label={authorizationCard.downloadLabel2}
							onClick={downloadDenialLetter}
							loading={isFetchingDenialLetter}
						/>
					</Fragment>
				) : null}

				{showSeeMyAppointments ? (
					<Button
						variant="contained"
						fullWidth
						onClick={() => navigate(MyAppointmentsEnum.MY_APPOINTMENTS)}
						data-tag-id={AutoSolEnum.AS_CLICK_VER_MEUS_AGENDAMENTOS}>
						{authorizationCard.seeMyAppointments}
					</Button>
				) : null}
			</AuthorizationCardContainer>

			{shouldRenderDrawer ? (
				<AuthStatusDrawer
					authorization={selectedAuthorization}
					openDrawer={isDrawerOpen}
					onOpenDrawer={onOpenStatusDrawer}
					onCloseDrawer={onCloseStatusDrawer}
				/>
			) : null}
		</Fragment>
	);
}
